// CompatibilityTab.tsx
import React, { useState } from "react";
import { Users } from "lucide-react";
import AstriaMexicoShell from "../AstriaMexicoShell";
import { useMexicoColors } from "../AstriaMexicoThemeContext";
import {
  AstriaMexicoResultCard,
  AstriaMexicoFieldBlock,
  MXLoadingState,
  MXErrorState,
  MXChatCta,
} from "../AstriaMexicoCard";
import {
  MXPersonFields,
  MXSubmitButton,
  PersonDetails,
  EMPTY_PERSON_DETAILS,
} from "../AstriaMexicoWizard";
import { useAstriaMexicoTab } from "../useAstriaMexicoTab";
import { AstriaMexicoCompatibilityData, Message } from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "Compatibility";

const buildMessage = (self: PersonDetails, partner: PersonDetails) => `
Lee nuestra Compatibilidad (cómo se encuentran nuestros ritmos, no un puntaje ni un veredicto).
Mi fecha de nacimiento: ${self.dob.trim()}.
Mi hora de nacimiento: ${self.time.trim() || "No proporcionada"}.
Mi ciudad de nacimiento: ${self.place.trim() || "No proporcionada"}.
Fecha de nacimiento de ${partner.name.trim() || "mi pareja"}: ${partner.dob.trim()}.
Hora de nacimiento de ${partner.name.trim() || "mi pareja"}: ${partner.time.trim() || "No proporcionada"}.
Ciudad de nacimiento de ${partner.name.trim() || "mi pareja"}: ${partner.place.trim() || "No proporcionada"}.
Por favor dame una lectura cálida sobre cómo se encuentran nuestros ritmos.
`.trim();

// Colors must be read INSIDE AstriaMexicoShell's subtree — these small
// components are rendered as `children`, not evaluated inline, so their hook
// calls resolve after the shell's theme provider has mounted.

const ResultView: React.FC<{
  data?: AstriaMexicoCompatibilityData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { colors } = useMexicoColors();

  if (data) {
    return (
      <>
        <AstriaMexicoResultCard
          icon={<Users className="w-5 h-5" />}
          title="Compatibilidad"
          subtitle="Dos ritmos, una lectura honesta"
          shareText={`${data.reading}\n\n${data.strengths}`}
        >
          <AstriaMexicoFieldBlock label="Lectura" value={data.reading} />
          <AstriaMexicoFieldBlock label="Fortalezas" value={data.strengths} />
          <AstriaMexicoFieldBlock label="Desafíos" value={data.challenges} />
        </AstriaMexicoResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          Empezar Una Nueva Lectura
        </button>
        <MXChatCta onClick={goToChat} />
      </>
    );
  }

  if (message) {
    return (
      <AstriaMexicoResultCard icon={<Users className="w-5 h-5" />} title="Compatibilidad">
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaMexicoResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  self: PersonDetails;
  setSelf: (v: PersonDetails) => void;
  partner: PersonDetails;
  setPartner: (v: PersonDetails) => void;
  onSubmit: () => void;
  formValid: boolean;
}> = ({ self, setSelf, partner, setPartner, onSubmit, formValid }) => {
  const { colors } = useMexicoColors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          Compatibilidad
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          Cómo se encuentran sus ritmos — nunca un puntaje ni un veredicto, solo una lectura
          honesta y cálida.
        </p>
      </div>

      <MXPersonFields
        heading="Tus Datos"
        values={self}
        onChange={setSelf}
        namePlaceholder="Tu nombre"
      />
      <MXPersonFields heading="Sus Datos" values={partner} onChange={setPartner} />

      <MXSubmitButton onClick={onSubmit} disabled={!formValid} label="Revelar Nuestra Compatibilidad" />
    </>
  );
};

const CompatibilityTab: React.FC = () => {
  const { loading, error, message, send, reset, goToChat } = useAstriaMexicoTab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [self, setSelf] = useState<PersonDetails>(EMPTY_PERSON_DETAILS);
  const [partner, setPartner] = useState<PersonDetails>(EMPTY_PERSON_DETAILS);

  const started = !!message || loading || !!error;
  const formValid = !!self.dob.trim() && !!partner.dob.trim();

  const handleSubmit = () => send(buildMessage(self, partner));

  const data = message?.astriaMexicoData as AstriaMexicoCompatibilityData | undefined;

  if (started) {
    return (
      <AstriaMexicoShell active="compatibility">
        {loading && <MXLoadingState label="Viendo cómo se encuentran sus ritmos…" />}
        {!loading && error && <MXErrorState message={error} onRetry={handleSubmit} />}
        {!loading && !error && (
          <ResultView data={data} message={message} reset={reset} goToChat={goToChat} />
        )}
      </AstriaMexicoShell>
    );
  }

  return (
    <AstriaMexicoShell active="compatibility">
      <IntroForm
        self={self}
        setSelf={setSelf}
        partner={partner}
        setPartner={setPartner}
        onSubmit={handleSubmit}
        formValid={formValid}
      />
    </AstriaMexicoShell>
  );
};

export default CompatibilityTab;
